import React, { useRef } from 'react';
import { Box, Button, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material';
import type { EmailCopy, EmailTypeSetting } from '../../types/email';
import { COPY_LIMITS, CopyField, copyErrors, sameCopy } from './emailCopy';

interface Props {
  type: EmailTypeSetting;
  value: EmailCopy;
  onChange: (next: EmailCopy) => void;
  disabled?: boolean;
}

const FIELDS: Array<{ key: CopyField; label: string; multiline?: boolean }> = [
  { key: 'subject', label: 'Subject' },
  { key: 'heading', label: 'Heading' },
  { key: 'body', label: 'Message', multiline: true },
  { key: 'button', label: 'Button' },
];

/**
 * A school's wording for one email: subject, heading, message and button.
 * Placeholder chips drop {Child name} and friends in at the cursor, without
 * taking focus from the field being written.
 */
const EmailCopyEditor: React.FC<Props> = ({ type, value, onChange, disabled }) => {
  const inputs = useRef<Partial<Record<CopyField, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const focused = useRef<CopyField>('body');
  const errors = copyErrors(type, value);
  const fields = FIELDS.filter(f => type.defaults[f.key] !== undefined);
  const isDefault = sameCopy(value, type.defaults);

  const insert = (token: string) => {
    const key = focused.current;
    const el = inputs.current[key];
    const text = value[key] ?? '';
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const piece = `{${token}}`;
    onChange({ ...value, [key]: text.slice(0, start) + piece + text.slice(end) });
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + piece.length, start + piece.length); });
  };

  return (
    <Stack spacing={2.25}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight={700}>Wording</Typography>
        <Button
          size="small" color="inherit" disabled={disabled || isDefault} onClick={() => onChange({ ...type.defaults })}
          sx={{ textTransform: 'none', color: 'text.secondary', mr: -1 }}
        >
          Reset to EduBook’s
        </Button>
      </Stack>

      {fields.map(f => (
        <TextField
          key={f.key}
          label={f.label}
          value={value[f.key] ?? ''}
          onChange={e => onChange({ ...value, [f.key]: e.target.value })}
          onFocus={() => { focused.current = f.key; }}
          inputRef={(el) => { inputs.current[f.key] = el; }}
          inputProps={{ maxLength: COPY_LIMITS[f.key] }}
          multiline={f.multiline}
          minRows={f.multiline ? 7 : undefined}
          size="small"
          fullWidth
          disabled={disabled}
          error={!!errors[f.key]}
          helperText={errors[f.key] ?? (f.multiline ? 'Leave a blank line between paragraphs.' : undefined)}
        />
      ))}

      <Box>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
          Placeholders · click to add at the cursor
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
          {type.placeholders.map(p => (
            <Tooltip key={p.token} title={p.hint} arrow>
              <Chip
                label={p.token}
                size="small"
                variant="outlined"
                disabled={disabled}
                onMouseDown={e => e.preventDefault()}
                onClick={() => insert(p.token)}
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.25 }}>
          Details, lists and links are always filled in from the record, whatever the wording.
        </Typography>
      </Box>
    </Stack>
  );
};

export default EmailCopyEditor;
